import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, API } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { 
  Users, 
  UserPlus,
  Building2,
  Copy,
  Check,
  AlertTriangle,
  Dumbbell,
  Moon,
  Calendar,
  CreditCard,
  Settings,
  LogOut,
  ChevronRight,
  TrendingUp,
  Trash2,
  Trophy
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import axios from 'axios';
import { toast } from 'sonner';

const OrganizationDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [athletes, setAthletes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviting, setInviting] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (user?.org_id) {
      fetchDashboardData();
      fetchAthletes();
    } else if (user && !user.org_id) {
      // User logged in but not an org admin
      setLoading(false);
    }
    
    // Fallback: if still loading after 5 seconds, stop
    const timeout = setTimeout(() => {
      if (loading) setLoading(false);
    }, 5000);
    
    return () => clearTimeout(timeout);
  }, [user]);

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('aura_token');
      const response = await axios.get(`${API}/organizations/${user.org_id}/dashboard`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setDashboardData(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAthletes = async () => {
    try {
      const token = localStorage.getItem('aura_token');
      const response = await axios.get(`${API}/organizations/${user.org_id}/athletes`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setAthletes(response.data.athletes || []);
    } catch (error) {
      console.error('Failed to fetch athletes:', error);
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    setInviting(true);
    try {
      const token = localStorage.getItem('aura_token');
      await axios.post(`${API}/organizations/${user.org_id}/invite`, {
        email: inviteEmail,
        name: inviteName
      }, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      toast.success('Invitation sent!');
      setShowInviteDialog(false);
      setInviteEmail('');
      setInviteName('');
      fetchAthletes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invite');
    } finally {
      setInviting(false);
    }
  };

  const handleRemoveAthlete = async (athleteId) => {
    if (!confirm('Remove this athlete from your organization?')) return;
    
    try {
      const token = localStorage.getItem('aura_token');
      await axios.delete(`${API}/organizations/${user.org_id}/athletes/${athleteId}`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      toast.success('Athlete removed');
      fetchAthletes();
      fetchDashboardData();
    } catch (error) {
      toast.error('Failed to remove athlete');
    }
  };

  const copyInviteCode = () => {
    navigator.clipboard.writeText(dashboardData?.organization?.invite_code || '');
    setCopied(true);
    toast.success('Invite code copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const getRecoveryColor = (score) => {
    if (!score) return 'text-aura-text-muted';
    if (score >= 80) return 'text-aura-success';
    if (score >= 60) return 'text-aura-secondary';
    if (score >= 40) return 'text-aura-warning';
    return 'text-aura-destructive';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-aura-bg flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-aura-secondary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-aura-bg">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-aura-bg/95 backdrop-blur-lg border-b border-white/5">
        <div className="flex items-center justify-between h-14 px-4 max-w-7xl mx-auto">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-aura-secondary rounded-sm flex items-center justify-center">
              <span className="font-heading font-black text-white text-lg">A</span>
            </div>
            <div>
              <span className="font-heading font-bold text-lg text-white tracking-tight">
                {dashboardData?.organization?.name || 'Organization'}
              </span>
              <Badge variant="outline" className="ml-2 text-xs capitalize">
                {dashboardData?.organization?.org_type}
              </Badge>
            </div>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2 p-2 rounded-lg hover:bg-white/5 transition-colors">
                <div className="w-8 h-8 rounded-full bg-aura-secondary flex items-center justify-center">
                  <span className="font-heading font-bold text-white text-sm">
                    {user?.name?.charAt(0)?.toUpperCase() || 'A'}
                  </span>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 bg-aura-surface border-white/10">
              <div className="px-3 py-2">
                <p className="font-medium text-white">{user?.name}</p>
                <p className="text-sm text-aura-text-muted">{user?.email}</p>
                <p className="text-xs text-aura-secondary mt-1">Organization Admin</p>
              </div>
              <DropdownMenuSeparator className="bg-white/10" />
              <DropdownMenuItem asChild>
                <Link to="/organization/settings" className="flex items-center gap-2 text-white cursor-pointer">
                  <Settings className="w-4 h-4" />
                  Organization Settings
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/organization/leaderboard" className="flex items-center gap-2 text-white cursor-pointer">
                  <Trophy className="w-4 h-4" />
                  Team Leaderboard
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-white/10" />
              <DropdownMenuItem 
                onClick={handleLogout}
                className="flex items-center gap-2 text-aura-destructive cursor-pointer"
              >
                <LogOut className="w-4 h-4" />
                Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Main Content */}
      <main className="px-4 py-6 max-w-7xl mx-auto space-y-6">
        {/* Welcome & Invite Code */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading font-bold text-2xl sm:text-3xl text-white uppercase tracking-tight">
              Team Dashboard
            </h1>
            <p className="text-aura-text-muted mt-1">
              Manage your athletes and track team performance
            </p>
          </div>
          
          <Card className="bg-aura-surface border-white/10">
            <CardContent className="p-4">
              <p className="text-xs text-aura-text-muted uppercase tracking-wider mb-2">Invite Code</p>
              <div className="flex items-center gap-2">
                <code className="font-mono font-bold text-xl text-aura-secondary">
                  {dashboardData?.organization?.invite_code}
                </code>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={copyInviteCode}
                  className="text-aura-text-muted hover:text-white"
                >
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
              <p className="text-xs text-aura-text-subtle mt-1">Share this code with your athletes</p>
            </CardContent>
          </Card>
        </div>

        {/* Billing Path Banner */}
        <Card className={`border-white/10 ${
          dashboardData?.billing?.path === 'org_funded' 
            ? 'bg-aura-secondary/10 border-aura-secondary/30' 
            : 'bg-aura-success/10 border-aura-success/30'
        }`}>
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <CreditCard className={`w-6 h-6 ${
                  dashboardData?.billing?.path === 'org_funded' ? 'text-aura-secondary' : 'text-aura-success'
                }`} />
                <div>
                  <p className="font-heading font-bold text-white uppercase">
                    {dashboardData?.billing?.path === 'org_funded' ? 'Org-Funded Plan' : 'Athlete-Funded Plan'}
                  </p>
                  <p className="text-sm text-aura-text-muted">
                    {dashboardData?.billing?.path === 'org_funded' 
                      ? `$${dashboardData?.billing?.platform_fee} base + $${dashboardData?.billing?.per_athlete}/athlete`
                      : 'Athletes pay $3/month individually'
                    }
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-xs text-aura-text-muted">Monthly Cost</p>
                <p className="font-heading font-bold text-2xl text-white">
                  ${dashboardData?.billing?.monthly_cost || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="bg-aura-surface border-white/10">
            <CardContent className="p-4">
              <div className="flex items-center gap-3 mb-2">
                <Users className="w-5 h-5 text-aura-secondary" />
                <span className="text-xs text-aura-text-muted uppercase tracking-wider">Athletes</span>
              </div>
              <p className="font-heading font-bold text-3xl text-white">
                {dashboardData?.stats?.athlete_count || 0}
              </p>
            </CardContent>
          </Card>

          <Card className="bg-aura-surface border-white/10">
            <CardContent className="p-4">
              <div className="flex items-center gap-3 mb-2">
                <Dumbbell className="w-5 h-5 text-aura-primary" />
                <span className="text-xs text-aura-text-muted uppercase tracking-wider">Workouts/Week</span>
              </div>
              <p className="font-heading font-bold text-3xl text-white">
                {dashboardData?.stats?.workouts_this_week || 0}
              </p>
            </CardContent>
          </Card>

          <Card className="bg-aura-surface border-white/10">
            <CardContent className="p-4">
              <div className="flex items-center gap-3 mb-2">
                <Moon className="w-5 h-5 text-aura-warning" />
                <span className="text-xs text-aura-text-muted uppercase tracking-wider">Avg Recovery</span>
              </div>
              <p className={`font-heading font-bold text-3xl ${getRecoveryColor(dashboardData?.stats?.avg_recovery_score)}`}>
                {dashboardData?.stats?.avg_recovery_score || '--'}
              </p>
            </CardContent>
          </Card>

          <Link to="/organization/leaderboard" className="block">
            <Card 
              data-testid="leaderboard-card"
              className="bg-gradient-to-br from-yellow-500/10 to-amber-500/10 border-yellow-500/30 cursor-pointer hover:border-yellow-500/50 transition-colors"
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-3 mb-2">
                  <Trophy className="w-5 h-5 text-yellow-400" />
                  <span className="text-xs text-yellow-400/80 uppercase tracking-wider">Leaderboard</span>
                </div>
                <p className="font-heading font-bold text-lg text-white flex items-center gap-2">
                  View Rankings
                  <ChevronRight className="w-4 h-4 text-yellow-400" />
                </p>
              </CardContent>
            </Card>
          </Link>
        </div>

        {/* Alerts */}
        {dashboardData?.stats?.low_recovery_athletes?.length > 0 && (
          <Card className="bg-aura-destructive/10 border-aura-destructive/30">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-aura-destructive flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-white">Low Recovery Alert</p>
                  <p className="text-sm text-aura-text-muted mt-1">
                    {dashboardData.stats.low_recovery_athletes.length} athlete(s) have low recovery scores:
                  </p>
                  <ul className="mt-2 space-y-1">
                    {dashboardData.stats.low_recovery_athletes.map((athlete, i) => (
                      <li key={i} className="text-sm text-white">
                        • {athlete.name}: <span className="text-aura-destructive">{athlete.recovery_score}/100</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Athletes List */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading font-bold text-lg text-white uppercase tracking-wider">
              Athletes ({athletes.length})
            </h2>
            <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
              <DialogTrigger asChild>
                <Button className="bg-aura-secondary hover:bg-aura-secondary/90 text-white" data-testid="invite-athlete-btn">
                  <UserPlus className="w-5 h-5 mr-2" />
                  Invite Athlete
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-aura-surface border-white/10 max-w-md">
                <DialogHeader>
                  <DialogTitle className="font-heading font-bold text-xl text-white uppercase">
                    Invite Athlete
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleInvite} className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wider text-aura-text-subtle">Athlete Name</Label>
                    <Input
                      value={inviteName}
                      onChange={(e) => setInviteName(e.target.value)}
                      placeholder="Enter athlete's name"
                      required
                      className="h-12 bg-aura-bg border-white/10 text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wider text-aura-text-subtle">Athlete Email</Label>
                    <Input
                      type="email"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      placeholder="athlete@email.com"
                      required
                      className="h-12 bg-aura-bg border-white/10 text-white"
                    />
                  </div>
                  
                  <div className="aura-card p-4 bg-aura-bg/50">
                    <p className="text-sm text-aura-text-muted mb-2">Or share your invite code:</p>
                    <div className="flex items-center gap-2">
                      <code className="font-mono font-bold text-lg text-aura-secondary">
                        {dashboardData?.organization?.invite_code}
                      </code>
                      <Button type="button" size="sm" variant="ghost" onClick={copyInviteCode}>
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={inviting}
                    className="w-full bg-aura-secondary hover:bg-aura-secondary/90 text-white font-heading uppercase tracking-wider h-12"
                  >
                    {inviting ? (
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      'Send Invitation'
                    )}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {athletes.length > 0 ? (
            <div className="grid gap-3">
              {athletes.map((athlete) => (
                <Card key={athlete.user_id} className="bg-aura-surface border-white/10">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full bg-aura-primary flex items-center justify-center">
                          {athlete.picture ? (
                            <img src={athlete.picture} alt="" className="w-12 h-12 rounded-full object-cover" />
                          ) : (
                            <span className="font-heading font-bold text-white text-lg">
                              {athlete.name?.charAt(0)?.toUpperCase()}
                            </span>
                          )}
                        </div>
                        <div>
                          <h3 className="font-medium text-white">{athlete.name}</h3>
                          <p className="text-sm text-aura-text-muted">{athlete.email}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-6">
                        <div className="text-center hidden sm:block">
                          <p className="text-xs text-aura-text-muted">Recovery</p>
                          <p className={`font-heading font-bold text-lg ${getRecoveryColor(athlete.latest_recovery?.recovery_score)}`}>
                            {athlete.latest_recovery?.recovery_score || '--'}
                          </p>
                        </div>
                        <div className="text-center hidden sm:block">
                          <p className="text-xs text-aura-text-muted">Workouts</p>
                          <p className="font-heading font-bold text-lg text-white">
                            {athlete.workouts_this_week || 0}
                          </p>
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleRemoveAthlete(athlete.user_id)}
                          className="text-aura-text-muted hover:text-aura-destructive"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="bg-aura-surface border-white/10">
              <CardContent className="p-8 text-center">
                <Users className="w-12 h-12 text-aura-text-subtle mx-auto mb-4" />
                <h3 className="font-heading font-bold text-lg text-white mb-2">No Athletes Yet</h3>
                <p className="text-aura-text-muted mb-4">
                  Invite athletes to join your organization using the invite code or email
                </p>
                <div className="flex items-center justify-center gap-2 mb-4">
                  <code className="font-mono font-bold text-2xl text-aura-secondary">
                    {dashboardData?.organization?.invite_code}
                  </code>
                  <Button size="sm" variant="ghost" onClick={copyInviteCode}>
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
                <Button 
                  onClick={() => setShowInviteDialog(true)}
                  className="bg-aura-secondary hover:bg-aura-secondary/90 text-white"
                >
                  <UserPlus className="w-5 h-5 mr-2" />
                  Invite Athletes
                </Button>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Upcoming Events */}
        <Card className="bg-aura-surface border-white/10">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading font-bold text-lg text-white uppercase tracking-wider">
                Team Schedule
              </CardTitle>
              <Link to="/schedule" className="text-aura-secondary text-sm font-medium flex items-center gap-1">
                View All <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            {dashboardData?.upcoming_events?.length > 0 ? (
              <div className="space-y-3">
                {dashboardData.upcoming_events.slice(0, 5).map((event, i) => (
                  <div key={i} className="flex items-center gap-4 p-3 rounded-lg bg-white/5">
                    <Calendar className="w-5 h-5 text-aura-secondary" />
                    <div>
                      <p className="font-medium text-white">{event.title}</p>
                      <p className="text-sm text-aura-text-muted">
                        {new Date(event.start_time).toLocaleDateString('en-US', {
                          weekday: 'short',
                          month: 'short',
                          day: 'numeric',
                          hour: 'numeric',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6">
                <Calendar className="w-10 h-10 text-aura-text-subtle mx-auto mb-2" />
                <p className="text-aura-text-muted">No upcoming events</p>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default OrganizationDashboard;
